import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardSidebar from '../components/DashboardSidebar';
import CountryFlag from '../components/CountryFlag';
import { Button } from '../components/ui/button';
import {
  Building2, FileText, CheckCircle, Clock, XCircle,
  DollarSign, TrendingUp, ArrowRight, Shield,
  Loader2, Briefcase, AlertCircle, Lock, Mail, Trash2, Pencil
} from 'lucide-react';
import { b2bApi, applicationsApi } from '../lib/api';
import { toast } from 'sonner';

const B2BDashboard = () => {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashRes, appsRes] = await Promise.all([
        b2bApi.getDashboard(),
        b2bApi.getApplications()
      ]);
      setDashboard(dashRes.data);
      setApplications(appsRes.data);
    } catch (err) {
      console.error('B2B dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (applicationId) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    try {
      await applicationsApi.delete(applicationId);
      toast.success('Application deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const partner = dashboard?.partner;
  const stats = dashboard?.stats;
  const isApproved = partner?.approval_status === 'approved';

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-500 text-white';
      case 'rejected': return 'bg-red-500 text-white';
      case 'submitted': case 'under_review': case 'processing': return 'bg-[#FF5A36] text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F0E6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF5A36]" />
      </div>
    );
  }

  // ============ NOT APPROVED STATE ============
  if (!isApproved) {
    const isPending = partner?.approval_status === 'pending';
    const isRejected = partner?.approval_status === 'rejected';
    const isSuspended = partner?.approval_status === 'suspended';

    return (
      <div className="min-h-screen bg-[#F5F0E6]">
        <DashboardSidebar role="b2b_partner" />
        <main className="dashboard-content p-6 lg:p-8 pt-20 lg:pt-8" data-testid="b2b-dashboard-pending">
          <div className="max-w-2xl mx-auto mt-12">
            {/* Status Card */}
            <div className="bg-white rounded-3xl border border-[#D1CBBF] overflow-hidden shadow-sm">
              {/* Header */}
              <div className={`p-8 text-center ${isPending ? 'bg-amber-500' : isRejected ? 'bg-red-500' : 'bg-gray-700'}`}>
                <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                  {isPending ? <Clock className="w-10 h-10 text-white" /> : isRejected ? <XCircle className="w-10 h-10 text-white" /> : <Lock className="w-10 h-10 text-white" />}
                </div>
                <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {isPending ? 'Approval Pending' : isRejected ? 'Registration Rejected' : 'Account Suspended'}
                </h1>
                <p className="text-white/80 text-sm">
                  {isPending
                    ? 'Your B2B partner registration is under review by our admin team.'
                    : isRejected
                      ? 'Unfortunately, your partner registration was not approved.'
                      : 'Your partner account has been suspended by the administrator.'}
                </p>
              </div>

              {/* Body */}
              <div className="p-8">
                {/* Company Info */}
                <div className="bg-[#F5F0E6] rounded-xl p-5 mb-6">
                  <div className="flex items-center gap-3 mb-3">
                    {partner?.partner_type === 'agent'
                      ? <Briefcase className="w-5 h-5 text-[#FF5A36]" />
                      : <Building2 className="w-5 h-5 text-[#FF5A36]" />}
                    <div>
                      <p className="font-bold text-[#0A1E40]">{partner?.company_name}</p>
                      <p className="text-xs text-[#4A5568] capitalize">{partner?.partner_type === 'agent' ? 'Travel Agent' : 'Corporate Partner'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-[#4A5568]">
                      <Mail className="w-3.5 h-3.5" /> {partner?.email}
                    </div>
                    {partner?.phone && (
                      <div className="flex items-center gap-2 text-[#4A5568]">
                        <AlertCircle className="w-3.5 h-3.5" /> {partner?.phone}
                      </div>
                    )}
                  </div>
                </div>

                {/* What happens next */}
                {isPending && (
                  <div className="space-y-4 mb-6">
                    <h3 className="font-semibold text-[#0A1E40] text-sm">What happens next?</h3>
                    {[
                      { step: '1', text: 'Our team reviews your business details and documents' },
                      { step: '2', text: 'You will receive approval notification (usually within 24-48 hours)' },
                      { step: '3', text: 'Once approved, you can start submitting visa applications for your clients' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-full bg-[#FF5A36]/10 flex items-center justify-center shrink-0 mt-0.5">
                          <span className="text-[#FF5A36] text-xs font-bold">{item.step}</span>
                        </div>
                        <p className="text-sm text-[#4A5568] leading-relaxed">{item.text}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Disabled Features Preview */}
                <div className="border border-[#E8E2D6] rounded-xl p-4 mb-6 opacity-40 pointer-events-none" data-testid="disabled-features">
                  <div className="flex items-center gap-2 mb-3">
                    <Lock className="w-4 h-4 text-[#4A5568]" />
                    <span className="text-xs font-semibold text-[#4A5568] uppercase tracking-wider">Features locked until approval</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {['Submit Applications', 'Client Dashboard', 'Partner Discounts', 'Commission Tracking'].map((feat, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-[#4A5568]">
                        <Lock className="w-3 h-3" /> {feat}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Contact CTA */}
                <div className="text-center">
                  <p className="text-sm text-[#4A5568] mb-3">Need help? Contact our partner support team.</p>
                  <Link to="/contact">
                    <Button variant="outline" className="border-[#D1CBBF]" data-testid="contact-support-btn">
                      Contact Support
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ============ APPROVED STATE ============
  return (
    <div className="min-h-screen bg-[#F5F0E6]">
      <DashboardSidebar role="b2b_partner" />
      <main className="dashboard-content p-6 lg:p-8 pt-20 lg:pt-8" data-testid="b2b-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Partner Dashboard
            </h1>
            <p className="text-[#4A5568] text-sm">{partner?.company_name} · {partner?.partner_type === 'agent' ? 'Travel Agent' : 'Corporate'}</p>
          </div>
          <Link to="/partner/apply">
            <Button className="bg-[#FF5A36] hover:bg-[#E0492B]" data-testid="new-application-btn">
              New Application <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>

        {/* Partner Info Card */}
        <div className="bg-white rounded-2xl border border-[#D1CBBF] overflow-hidden mb-6" data-testid="partner-info-card">
          <div className="grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-[#E8E2D6]">
            <div className="p-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center shrink-0">
                <Shield className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-[#4A5568]">Status</p>
                <p className="font-bold text-green-600">Verified Partner</p>
              </div>
            </div>
            <div className="p-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FF5A36]/10 flex items-center justify-center shrink-0">
                <DollarSign className="w-5 h-5 text-[#FF5A36]" />
              </div>
              <div>
                <p className="text-xs text-[#4A5568]">Your Discount</p>
                <p className="font-bold text-[#0A1E40] text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{partner?.discount_percentage || 0}%</p>
              </div>
            </div>
            <div className="p-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center shrink-0">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-[#4A5568]">Commission Rate</p>
                <p className="font-bold text-[#0A1E40] text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{partner?.commission_rate || 0}%</p>
              </div>
            </div>
            <div className="p-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#0A1E40]/10 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-[#0A1E40]" />
              </div>
              <div>
                <p className="text-xs text-[#4A5568]">Partner Type</p>
                <p className="font-bold text-[#0A1E40]">{partner?.partner_type === 'agent' ? 'Travel Agent' : 'Corporate'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
            {[
              { label: 'Total Apps', value: stats.total_applications, icon: FileText, color: 'text-[#0A1E40]' },
              { label: 'Processing', value: stats.processing, icon: Clock, color: 'text-[#FF5A36]' },
              { label: 'Approved', value: stats.approved, icon: CheckCircle, color: 'text-green-600' },
              { label: 'Rejected', value: stats.rejected, icon: XCircle, color: 'text-red-500' },
              { label: 'Revenue', value: `₹${stats.total_revenue?.toLocaleString() || 0}`, icon: DollarSign, color: 'text-[#0A1E40]' },
              { label: 'Discount', value: `₹${stats.discount_saved?.toLocaleString() || 0}`, icon: Shield, color: 'text-[#FF5A36]' },
              { label: 'Commission', value: `₹${stats.commission_earned?.toLocaleString() || 0}`, icon: TrendingUp, color: 'text-green-600' },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-xl border border-[#D1CBBF] p-4" data-testid={`stat-${s.label.toLowerCase().replace(' ','-')}`}>
                <div className="flex items-center gap-2 mb-2">
                  <s.icon className={`w-4 h-4 ${s.color}`} />
                  <span className="text-xs text-[#4A5568]">{s.label}</span>
                </div>
                <p className={`text-xl font-bold ${s.color}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Client Applications */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>Client Applications</h2>
            <span className="text-xs text-[#4A5568] bg-white px-3 py-1 rounded-full border border-[#D1CBBF]">{applications.length} total</span>
          </div>
          
          {applications.length === 0 ? (
            <div className="bg-white rounded-2xl border border-[#D1CBBF] p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#F5F0E6] flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-[#D1CBBF]" />
              </div>
              <h3 className="font-bold text-[#0A1E40] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No Applications Yet</h3>
              <p className="text-[#4A5568] text-sm mb-5 max-w-sm mx-auto">Start by submitting a visa application for your client.</p>
              <Link to="/partner/apply">
                <Button className="bg-[#FF5A36] hover:bg-[#E0492B] rounded-xl px-6">
                  Submit First Application <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4" data-testid="b2b-applications-list">
              {applications.map(app => {
                const disc = app.discount_applied || 0;
                const comm = partner?.commission_rate || 0;
                const commAmount = app.payment_status === 'completed' ? Math.round((app.total_fee || 0) * (comm / 100)) : 0;
                const clientName = app.client_name || `${app.travelers?.[0]?.first_name || ''} ${app.travelers?.[0]?.last_name || ''}`.trim() || 'Client';
                const isPaid = app.payment_status === 'completed';
                
                return (
                  <div key={app.application_id} className="bg-white rounded-2xl border border-[#D1CBBF] overflow-hidden hover:shadow-md transition-all" data-testid={`b2b-app-${app.application_id}`}>
                    <div className="p-5">
                      <div className="flex items-start justify-between gap-4 mb-4">
                        {/* Left: Client + Country */}
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-11 h-11 rounded-xl bg-[#0A1E40] flex items-center justify-center shrink-0">
                            <span className="text-white font-bold text-sm">{clientName.charAt(0).toUpperCase()}</span>
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-bold text-[#0A1E40] truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{clientName}</h3>
                            <div className="flex items-center gap-2 text-xs text-[#4A5568]">
                              <CountryFlag code={app.country?.code} size="xs" />
                              <span>{app.country?.name}</span>
                              <span className="text-[#D1CBBF]">·</span>
                              <span>{app.visa_type?.name}</span>
                            </div>
                          </div>
                        </div>
                        {/* Right: Status + Actions */}
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold capitalize ${getStatusColor(app.status)}`}>
                            {app.status?.replace('_', ' ')}
                          </span>
                          {!isPaid && (
                            <>
                              <Link to={`/partner/apply?edit=${app.application_id}`}>
                                <Button size="sm" variant="outline" className="h-8 px-2 border-[#D1CBBF] text-[#4A5568] hover:text-[#FF5A36] hover:border-[#FF5A36]" data-testid={`edit-${app.application_id}`}>
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                              </Link>
                              <Button size="sm" variant="outline" className="h-8 px-2 border-[#D1CBBF] text-[#4A5568] hover:text-red-500 hover:border-red-300" onClick={() => handleDelete(app.application_id)} data-testid={`delete-${app.application_id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Info Row */}
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 bg-[#F5F0E6] rounded-xl p-3">
                        <div>
                          <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">App ID</p>
                          <Link to={`/applications/${app.application_id}`} className="text-xs text-[#FF5A36] font-bold hover:underline">{app.application_id}</Link>
                        </div>
                        <div>
                          <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">Travelers</p>
                          <p className="text-xs font-bold text-[#0A1E40]">{app.num_travelers}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">Fee</p>
                          <p className="text-xs font-bold text-[#0A1E40]">₹{app.total_fee?.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">Discount</p>
                          <p className="text-xs font-bold text-green-600">{disc > 0 ? `${disc}%` : '—'}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">Commission</p>
                          <p className="text-xs font-bold text-green-600">{commAmount > 0 ? `₹${commAmount.toLocaleString()}` : '—'}</p>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#F5F0E6]">
                        <div className="flex items-center gap-4 text-xs text-[#4A5568]">
                          <span>Travel: {new Date(app.travel_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                          <span className="text-[#D1CBBF]">·</span>
                          <span>Created: {new Date(app.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                          {isPaid && (
                            <>
                              <span className="text-[#D1CBBF]">·</span>
                              <span className="text-green-600 font-semibold flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Paid</span>
                            </>
                          )}
                        </div>
                        <Link to={`/applications/${app.application_id}`} className="text-xs text-[#FF5A36] font-semibold hover:underline flex items-center gap-1">
                          View Details <ArrowRight className="w-3 h-3" />
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default B2BDashboard;
